import { useFieldArray, useFormContext } from "react-hook-form";
import TranslationWrapper from "../TranslationWrapper/TranslationWrapper";
import { ImageResolutionHint } from "../ImageHints/ImageResolutionHint";
import { IconButton, TextField, Typography } from "@mui/material";
import { Add, Backspace } from "@mui/icons-material";
import React from "react";
import { makeStyles } from "tss-react/mui";
import distanceCostSchemeStyle from "../DistanceCostScheme/DistanceCostSchemeStyle";

const useStyles = makeStyles()(distanceCostSchemeStyle);

export interface ScheduledHomescreenVideo {
  startTime: string;
  stopTime: string;
  file?: File;
  // ids are assigned by the database. When creating a new timespan, it won't have an ID until saved
  id?: number;
  // from server, if the row already exists. Use for preview purposes only
  readonly attached_media?: {
    mime_type: string;
    original_url: string;
  };
}

interface ScheduledVideos {
  scheduled_homescreen_videos: ScheduledHomescreenVideo[];
}

export function ScheduledHomescreenMedia() {
  const { control, register } = useFormContext<ScheduledVideos>();
  const {
    fields: timeSpans,
    append,
    remove,
    update,
  } = useFieldArray<ScheduledVideos>({
    name: "scheduled_homescreen_videos",
    control,
  });
  const { classes: styles } = useStyles();

  return (
    <div>
      {timeSpans.length > 0 ? (
        <table className={styles.distanceTable} style={{ width: "initial" }}>
          <thead>
            <tr>
              <th>
                <TranslationWrapper tDefault={"From"} tKey={"timerange.from"} />
              </th>
              <th>
                <TranslationWrapper tDefault={"To"} tKey={"timerange.to"} />
              </th>
              <th>
                <TranslationWrapper tDefault={"Video or image"} tKey={"schedulable-media.header.video-or-image"} />
                <ImageResolutionHint res={"1080x1920"} />
              </th>
              <th />
            </tr>
          </thead>
          <tbody>
            {timeSpans.map((field, idx) => {
              const fileRegisterFields = register(
                `scheduled_homescreen_videos.${idx}.file` as const,
                field.attached_media ? {} : { required: "A media file is required" }
              );
              return (
                <tr key={field.id}>
                  <td>
                    <TextField type={"time"} {...register(`scheduled_homescreen_videos.${idx}.startTime`)} />
                  </td>
                  <td>
                    <TextField type={"time"} {...register(`scheduled_homescreen_videos.${idx}.stopTime`)} />
                  </td>
                  <td style={{ textAlign: "center", padding: "8px" }}>
                    {/*
                    The preview on file select doesn't seem to work due to react-hook-form preventing rerenders.
                    I don't know how to handle this correctly.
                    The preview now shows after upload. Which is good enough for now.
                    */}
                    {field.file && field.startTime === "remove this comparison when you know the fix" ? (
                      <>
                        <video
                          controls
                          src={URL.createObjectURL(field.file)}
                          style={{ maxWidth: "216px", maxHeight: "384px" }}
                        />
                        <hr />
                      </>
                    ) : field.attached_media ? (
                      <>
                        {field.attached_media.mime_type.includes("video") ? (
                          <video
                            controls
                            preload={"metadata"}
                            src={field.attached_media.original_url}
                            style={{ maxWidth: "216px", maxHeight: "384px" }}
                          />
                        ) : field.attached_media.mime_type.includes("image") ? (
                          <img
                            alt={"An image scheduled to show at a certain time on the kiosk home screen"}
                            src={field.attached_media.original_url}
                            style={{ maxWidth: "216px", maxHeight: "384px" }}
                          />
                        ) : (
                          `Your browser can't display the media of type ${field.attached_media.mime_type}`
                        )}
                        <hr />
                      </>
                    ) : null}
                    <input type={"file"} {...fileRegisterFields} accept={"video/*, image/*"} />
                  </td>
                  <td>
                    <IconButton
                      onClick={() => {
                        remove(idx);
                      }}
                      size={"small"}
                    >
                      <Backspace fontSize={"small"} style={{ color: "red" }} />
                    </IconButton>
                  </td>
                </tr>
              );
            })}
            <tr>
              <td colSpan={4} style={{ textAlign: "left" }}>
                <IconButton
                  onClick={() => {
                    append({ startTime: "00:00", stopTime: "00:00" });
                  }}
                  size={"small"}
                >
                  <Add style={{ color: "green" }} />
                </IconButton>
              </td>
            </tr>
          </tbody>
        </table>
      ) : (
        <>
          <Typography variant={"body1"}>
            <TranslationWrapper
              tDefault={"Display different media on the kiosk homescreen depending on what time it is."}
              tKey={"vestigingen.form.kiosk.schedulableHomescreenMedia.description"}
            />
          </Typography>
          <IconButton
            onClick={() => {
              append({ startTime: "00:00", stopTime: "00:00" });
            }}
          >
            <Add style={{ color: "green" }} />
          </IconButton>
        </>
      )}
    </div>
  );
}
